<template>
  <div class="stock-rsi">
    <div class="page-header">
      <div class="page-title">
        <div class="title-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h1>RSI相对强弱指标</h1>
        <div class="title-decoration"></div>
      </div>
      <p class="page-subtitle">6日RSI指标，识别超买超卖</p>
    </div>

    <div class="filter-bar">
      <el-select
        v-model="selectedStockCode"
        placeholder="请选择股票"
        filterable
        @change="handleStockChange"
        class="stock-select"
        v-loading="stockListLoading"
      >
        <el-option
          v-for="item in stockList"
          :key="item.stock_code"
          :label="`${item.stock_name} (${item.stock_code})`"
          :value="item.stock_code"
        >
        </el-option>
      </el-select>

      <el-date-picker
        v-model="startDate"
        type="date"
        placeholder="开始日期"
        value-format="yyyy-MM-dd"
        @change="fetchData"
      />
      <span>至</span>
      <el-date-picker
        v-model="endDate"
        type="date"
        placeholder="结束日期"
        value-format="yyyy-MM-dd"
        @change="fetchData"
      />
      <el-button type="primary" @click="fetchData">查询</el-button>
    </div>

    <div ref="rsiChart" class="rsi-chart" v-loading="loading"></div>

    <!-- RSI 指标说明 -->
    <div class="rsi-legend">
      <div class="legend-item">
        <span class="legend-color overbought"></span>
        <span>超买区 (RSI > 70)</span>
      </div>
      <div class="legend-item">
        <span class="legend-color oversold"></span>
        <span>超卖区 (RSI < 30)</span>
      </div>
      <div class="legend-item">
        <span class="legend-color neutral"></span>
        <span>中性区 (30 - 70)</span>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'

export default {
  name: 'StockRSI',
  data() {
    return {
      chart: null,
      loading: false,
      startDate: this.formatDate(new Date(Date.now() - 360 * 24 * 60 * 60 * 1000)), // 默认6个月前
      endDate: this.formatDate(new Date()),
      rsiData: [],
      stockList: [],
      stockListLoading: false,
      selectedStockCode: '',
      currentStockName: '',
      storageKey: 'selected_stock_code'
    }
  },
  watch: {
    '$route.query.code': {
      immediate: true,
      handler(newCode) {
        if (newCode) {
          const selectedStock = this.stockList.find(item => item.stock_code === newCode)
          if (selectedStock) {
            this.currentStockName = selectedStock.stock_name
          }
          if (newCode !== this.selectedStockCode) {
            this.selectedStockCode = newCode
            localStorage.setItem(this.storageKey, newCode)
            this.fetchStockList()
            this.fetchDataWithCode(newCode)
          }
        }
      }
    },
    selectedStockCode(newVal) {
      if (newVal) {
        localStorage.setItem(this.storageKey, newVal)
      }
    }
  },
  mounted() {
    this.initChart()
    this.restoreSelectedCode()
    if (!this.$route.query.code && !this.selectedStockCode) {
      this.selectedStockCode = 'sz.002602'
    }
    if (this.selectedStockCode) {
      this.fetchStockList()
      this.fetchData()
    }
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    initChart() {
      this.chart = echarts.init(this.$refs.rsiChart)
      const option = this.getChartOption()
      this.chart.setOption(option)
    },

    async fetchStockList() {
      this.stockListLoading = true
      try {
        const response = await axios.get('/stock/info/list')
        if (response.data.status === 'success' && response.data.data) {
          this.stockList = response.data.data
          // 更新当前股票名称
          if (this.selectedStockCode) {
            const selectedStock = this.stockList.find(item => item.stock_code === this.selectedStockCode)
            if (selectedStock) {
              this.currentStockName = selectedStock.stock_name
            }
          }
        } else {
          this.stockList = []
        }
      } catch (error) {
        console.error('获取股票列表失败:', error)
        this.$message.error('获取股票列表失败')
        this.stockList = []
      } finally {
        this.stockListLoading = false
      }
    },

    restoreSelectedCode() {
      const savedCode = localStorage.getItem(this.storageKey)
      if (savedCode) {
        this.selectedStockCode = savedCode
      }
    },

    handleStockChange(code) {
      this.$router.push({ query: { code } })
    },

    async fetchDataWithCode(code) {
      this.loading = true
      try {
        const params = {
          start_date: this.startDate,
          end_date: this.endDate
        }

        const response = await axios.get(`/stock/rsi/${code}`, { params })

        if (response.data.status === 'success' && response.data.data) {
          this.rsiData = response.data.data
          this.updateChart()
        } else {
          this.$message.error('返回状态异常: ' + (response.data.message || '未知错误'))
        }
      } catch (error) {
        console.error('获取RSI数据失败:', error)
        this.$message.error('获取RSI数据失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },

    async fetchData() {
      this.fetchDataWithCode(this.selectedStockCode)
    },

    updateChart() {
      if (!this.chart || this.rsiData.length === 0) return

      const dates = this.rsiData.map(item => item.date)
      const rsiData = this.rsiData.map(item => parseFloat(item.rsi))
      const closeData = this.rsiData.map(item => parseFloat(item.close))

      const option = {
        title: {
          text: `${this.currentStockName || this.selectedStockCode} RSI(6)指标`,
          left: 'center',
          textStyle: {
            color: '#cbd5e1',
            fontSize: 16
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: (params) => {
            const dataIndex = params[0].dataIndex
            const data = this.rsiData[dataIndex]
            const rsi = parseFloat(data.rsi)
            let status = '中性'
            if (rsi > 70) status = '超买'
            else if (rsi < 30) status = '超卖'
            return `
              <div style="padding: 8px;">
                <div style="font-weight: bold; margin-bottom: 4px;">${data.date}</div>
                <div>收盘价: ${data.close ? parseFloat(data.close).toFixed(2) : '-'}</div>
                <div>RSI(6): <span style="color: ${rsi > 70 ? '#ef5350' : rsi < 30 ? '#26a69a' : '#a5b4fc'}; font-weight: bold;">${!isNaN(rsi) ? rsi.toFixed(2) : '-'}</span></div>
                <div>状态: ${status}</div>
              </div>
            `
          }
        },
        legend: {
          data: ['RSI(6)', '收盘价'],
          top: 30,
          textStyle: {
            color: '#94a3b8'
          }
        },
        grid: [
          {
            left: '10%',
            right: '10%',
            top: '15%',
            height: '50%'
          },
          {
            left: '10%',
            right: '10%',
            top: '70%',
            height: '20%'
          }
        ],
        xAxis: [
          {
            type: 'category',
            data: dates,
            boundaryGap: false,
            axisLine: { onZero: false, lineStyle: { color: '#475569' } },
            splitLine: { show: false },
            axisLabel: {
              color: '#94a3b8',
              formatter: (value) => {
                const parts = value.split('-')
                if (parts.length === 3) {
                  return `${parts[1]}/${parts[2]}`
                }
                return value
              }
            }
          },
          {
            type: 'category',
            gridIndex: 1,
            data: dates,
            boundaryGap: false,
            axisLine: { onZero: false, lineStyle: { color: '#475569' } },
            splitLine: { show: false },
            axisLabel: { show: false }
          }
        ],
        yAxis: [
          {
            scale: true,
            min: 0,
            max: 100,
            splitArea: { show: false },
            axisLine: { show: true, lineStyle: { color: '#475569' } },
            axisLabel: { show: true, color: '#94a3b8' },
            splitLine: { show: false }
          },
          {
            scale: true,
            gridIndex: 1,
            splitNumber: 2,
            axisLabel: { show: false },
            axisLine: { show: false },
            splitLine: { show: false }
          }
        ],
        dataZoom: [
          {
            type: 'inside',
            xAxisIndex: [0, 1],
            start: 0,
            end: 100
          },
          {
            show: true,
            xAxisIndex: [0, 1],
            type: 'slider',
            top: '92%',
            start: 0,
            end: 100
          }
        ],
        series: [
          {
            name: 'RSI(6)',
            type: 'line',
            data: rsiData,
            smooth: true,
            lineStyle: {
              opacity: 0.8,
              width: 2,
              color: '#a5b4fc'
            },
            itemStyle: {
              color: '#a5b4fc'
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(165, 180, 252, 0.3)' },
                  { offset: 1, color: 'rgba(165, 180, 252, 0.05)' }
                ]
              }
            },
            showSymbol: false,
            markArea: {
              silent: true,
              itemStyle: {
                opacity: 0.15
              },
              label: { show: false },
              data: [
                [
                  { yAxis: 70, itemStyle: { color: '#ef5350' } },
                  { yAxis: 100 }
                ],
                [
                  { yAxis: 30, itemStyle: { color: '#a5b4fc' } },
                  { yAxis: 70 }
                ],
                [
                  { yAxis: 0, itemStyle: { color: '#26a69a' } },
                  { yAxis: 30 }
                ]
              ]
            },
            markLine: {
              silent: true,
              symbol: ['none', 'none'],
              lineStyle: {
                type: 'dashed',
                width: 1,
                opacity: 0.6
              },
              label: { show: false },
              data: [
                { yAxis: 70, lineStyle: { color: '#ef5350', opacity: 0.8 } },
                { yAxis: 30, lineStyle: { color: '#26a69a', opacity: 0.8 } }
              ]
            }
          },
          {
            name: '收盘价',
            type: 'line',
            xAxisIndex: 1,
            yAxisIndex: 1,
            data: closeData,
            smooth: true,
            lineStyle: {
              opacity: 0.6,
              width: 1.5,
              color: '#94a3b8'
            },
            itemStyle: {
              color: '#94a3b8'
            },
            showSymbol: false
          }
        ]
      }

      this.chart.setOption(option, true)
    },

    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    handleResize() {
      if (this.chart) {
        this.chart.resize()
      }
    },

    getChartOption() {
      return {
        title: {
          text: '加载中...',
          left: 'center'
        }
      }
    }
  }
}
</script>

<style scoped>
.stock-rsi {
  padding: 20px;
  min-height: calc(100vh - 104px);
  position: relative;
}

.page-header {
  text-align: center;
  margin-bottom: 32px;
  position: relative;
  z-index: 1;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 8px;
}

.title-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a5b4fc;
  border: 1px solid rgba(99, 102, 241, 0.3);
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.15);
}

.title-icon svg {
  width: 24px;
  height: 24px;
}

.page-title h1 {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 50%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 1px;
}

.title-decoration {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, transparent 0%, #6366f1 50%, transparent 100%);
  border-radius: 2px;
}

.page-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  letter-spacing: 0.5px;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
  position: relative;
  z-index: 1;
}

.stock-select {
  width: 250px;
}

.stock-select :deep(.el-input__inner),
.filter-bar :deep(.el-date-picker .el-input__inner) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #e2e8f0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.stock-select :deep(.el-input__inner):hover,
.filter-bar :deep(.el-date-picker .el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4);
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.15);
}

.stock-select :deep(.el-input__inner):focus,
.filter-bar :deep(.el-date-picker .el-input__inner):focus {
  border-color: #6366f1;
  box-shadow: 0 0 16px rgba(99, 102, 241, 0.25);
}

.filter-bar :deep(.el-date-picker .el-input__prefix) {
  color: rgba(99, 102, 241, 0.6);
}

.filter-bar span {
  color: #64748b;
  font-size: 14px;
}

.filter-bar :deep(.el-button--primary) {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-button--primary):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(99, 102, 241, 0.45);
}

.filter-bar :deep(.el-button--primary):active {
  transform: translateY(0);
}

.rsi-chart {
  width: 100%;
  height: 650px;
  background: rgba(15, 23, 42, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 16px;
  position: relative;
  z-index: 1;
}

.rsi-legend {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 20px;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  position: relative;
  z-index: 1;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 14px;
}

.legend-color {
  width: 24px;
  height: 4px;
  border-radius: 2px;
}

.legend-color.overbought {
  background: #ef5350;
}

.legend-color.oversold {
  background: #26a69a;
}

.legend-color.neutral {
  background: linear-gradient(90deg, #26a69a 0%, #a5b4fc 50%, #ef5350 100%);
}
</style>
